/*
 * Defensive patches for the cart drawer: two account-level Script Tag apps that
 * reach into it (Rebuy, ShipInsure) and one stock-Dawn bug it trips over.
 *
 * 1-2 keep Rebuy's Smart Cart out of our drawer.
 *
 * Rebuy is NOT installed by this theme. It arrives as an account-level Script
 * Tag (cdn.rebuyengine.com/onsite/js/rebuy.js?shop=...), which Shopify injects
 * on every storefront request regardless of which theme is live. So there is
 * nothing to delete from the repo: `grep -ri rebuy` over layout/, sections/,
 * snippets/ and assets/ turns up only this theme's own bridge file.
 *
 * Two things had to be handled, and they need different fixes.
 *
 * 1. The click. Rebuy binds a **document capture-phase** listener and calls
 *    stopPropagation() on it. Dawn's own listener is on the cart link itself,
 *    which is the target phase — later — so it never ran. Measured: clicking
 *    #cart-icon-bubble reached a window-capture probe 3 times and a document
 *    BUBBLE probe 0 times, and opened BOTH carts at once.
 *
 *    window capture is the one place that precedes document capture, so that is
 *    where this listener lives. It is scoped to the cart trigger, so no other
 *    click on the page is affected.
 *
 * 2. Everything else. The guard only covers the header link; Rebuy would still
 *    open its cart from its own triggers. `Rebuy.SmartCart.unbindEvents()`
 *    releases those. Deliberately NOT `destroy()`: destroy() also strips every
 *    .rebuy-widget node from the page, while unbindEvents() leaves the cart node
 *    and all widgets intact and simply stops it listening (verified: widgets 1
 *    before, 1 after; cart node still in the DOM; only our drawer opens).
 *
 * Rebuy loads async, so unbind is retried until it lands, and re-applied when
 * Rebuy fires its own cart events in case a re-init re-binds them.
 *
 * NOTE: this is defence, not the cure. The real fix is switching Smart Cart off
 * in the Rebuy app's own settings. This file exists so the storefront behaves
 * before that happens, and stays correct if it is ever switched back on.
 */

(function () {
  var CART_TRIGGER = '#cart-icon-bubble';
  var RETRY_MS = 150;
  var GIVE_UP_MS = 20000;

  /* 1 -------------------------------------------------------------------- */

  window.addEventListener(
    'click',
    function (event) {
      if (event.defaultPrevented) return;
      // Let modified clicks (new tab, download, …) behave normally.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (typeof event.button === 'number' && event.button !== 0) return;

      var target = event.target;
      var trigger = target && target.closest ? target.closest(CART_TRIGGER) : null;
      if (!trigger) return;

      var drawer = document.querySelector('cart-drawer');
      // No drawer on the page (cart_type is page/notification, or the element
      // never upgraded) — leave the link alone so it still navigates to /cart.
      if (!drawer || typeof drawer.open !== 'function') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      drawer.open(trigger);
    },
    true
  );

  /* 2 -------------------------------------------------------------------- */

  function unbindSmartCart() {
    try {
      var smartCart = window.Rebuy && window.Rebuy.SmartCart;
      if (!smartCart || typeof smartCart.unbindEvents !== 'function') return false;
      smartCart.unbindEvents();
      return true;
    } catch (e) {
      /* Rebuy's internals are not ours to depend on; never break the drawer. */
      return false;
    }
  }

  if (!unbindSmartCart()) {
    var started = Date.now();
    var timer = setInterval(function () {
      if (unbindSmartCart() || Date.now() - started > GIVE_UP_MS) clearInterval(timer);
    }, RETRY_MS);
  }

  // Re-apply if Rebuy re-binds during a cart change of its own.
  ['rebuy:cart.change', 'rebuy:cart.add', 'rebuy:smartcart.init'].forEach(function (name) {
    document.addEventListener(name, function () {
      setTimeout(unbindSmartCart, 0);
    });
  });

  /* 3 -------------------------------------------------------------------- */

  /*
   * ShipInsure — also an account-level Script Tag — does two things to the
   * footer. Its own injected containers are hidden in CSS, but it ALSO rewrites
   * our checkout button's text in place ("VIP CHECKOUT | $129.28") and stamps a
   * `_si-checkout-*` class onto it, so markup alone cannot hold the label.
   *
   * The button carries the intended text in data-cd-checkout-label; this puts it
   * back whenever it changes. Restoring is a no-op once the text already matches,
   * so the observer settles rather than looping.
   */
  function restoreCheckoutLabel() {
    var button = document.querySelector('.cd-checkout[data-cd-checkout-label]');
    if (!button) return;

    var wanted = button.getAttribute('data-cd-checkout-label');
    var label = button.querySelector('.cd-checkout__label');

    if (label && label.textContent.trim() === wanted.trim()) return;

    if (!label) {
      button.textContent = '';
      label = document.createElement('span');
      label.className = 'cd-checkout__label';
      button.appendChild(label);
    }
    label.textContent = wanted;
  }

  var drawer = document.querySelector('cart-drawer');
  if (drawer && 'MutationObserver' in window) {
    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      // setTimeout, not requestAnimationFrame: rAF is frozen while the tab is
      // backgrounded, which left the app's label in place until the tab was
      // looked at again.
      setTimeout(function () {
        scheduled = false;
        restoreCheckoutLabel();
      }, 0);
    });
    // The element itself survives every render; only its innards are replaced.
    observer.observe(drawer, { childList: true, subtree: true, characterData: true });
    restoreCheckoutLabel();
  }

  /* 4 -------------------------------------------------------------------- */

  /*
   * Dawn's resetQuantityInput is blind to the drawer.
   *
   * cart.js validateQuantity() rejects any value below data-min and routes it
   * through setValidity() -> resetQuantityInput(index), which looks the input up
   * as `#Quantity-${id}` and nothing else. Every other lookup in that file
   * carries the drawer's id as a fallback (`#Quantity-N` || `#Drawer-quantity-N`,
   * at cart.js:176 and :207) — this one does not, so inside the drawer it
   * resolves to null and throws "Cannot read properties of null (reading
   * 'getAttribute')" BEFORE the /cart/change.js request is made. Measured: the
   * row stayed on screen reading 0 while the cart still held 1.
   *
   * The stepper can no longer reach a sub-min value — min now matches data-min
   * in cart-drawer.liquid — but the field is still a type=number a customer can
   * type 0 into, so the path stays reachable. With this in place a typed 0
   * snaps back to the line's real quantity and reports the min error, which is
   * what the same input already does on the cart page.
   *
   * Overridden on CartDrawerItems rather than patched into cart.js so that file
   * stays byte-identical to the Dawn 15.5.0 import. Delete this section if Dawn
   * adds the fallback upstream.
   */
  if ('customElements' in window) {
    customElements.whenDefined('cart-drawer-items').then(function () {
      var CartDrawerItems = customElements.get('cart-drawer-items');
      if (!CartDrawerItems) return;
      // Own property, not inherited: if this ever lands upstream, leave it be.
      if (Object.prototype.hasOwnProperty.call(CartDrawerItems.prototype, 'resetQuantityInput')) return;

      CartDrawerItems.prototype.resetQuantityInput = function (id) {
        var input = this.querySelector('#Quantity-' + id) || this.querySelector('#Drawer-quantity-' + id);
        if (!input) return;
        input.value = input.getAttribute('value');
        this.isEnterPressed = false;
      };
    });
  }

  /*
   * 5. Shopify's own "Buy X Get Y" discount engine can silently split a cart
   * line: once a purchase starts counting toward a discount's "customer
   * buys" requirement, the units already in the cart stay on one line and
   * the rest land on a fresh one — same variant, same visible properties,
   * a different internal hash. Verified live 2026-09-23 with raw XHR (no
   * theme JS in the loop at all): setting an existing line's quantity past
   * the $100 free-gift threshold split 5 units into 1 + 4 the instant the
   * gift line landed, and every attempt to consolidate them back into one
   * line was undone by Shopify on the very next write to that variant — it
   * will not let the discount-allocated portion grow past what its own math
   * decided, and silently reroutes the rest onto whichever line it treats
   * as flexible. This cannot be fixed by writing the cart differently; the
   * theme can only stop assuming "one variant = one line".
   *
   * cart-drawer.liquid groups matching lines (variant + properties +
   * selling plan) into one visual row and hands the quantity input a JSON
   * membership list via data-quantity-group-lines. This makes the stepper
   * group-aware: a change is expressed as the new TOTAL, walked across the
   * group's lines the same shape as cart-gift.js's trimTo() (each line keeps
   * up to what's left of the target, in order), then submitted as one
   * /cart/change.js call per line, addressed by line KEY — Shopify's
   * /cart/update.js "updates" object silently no-ops when keyed by the full
   * line key instead of a plain line number or variant id, verified live.
   * Whichever line Shopify actually grows, refreshDawnCartUI() re-renders
   * from a fresh /cart.js read afterward, so the row just shows the new sum.
   *
   * Falls through to Dawn's own updateQuantity untouched for every ordinary
   * (non-split) line — the JSON attribute only lists more than one line when
   * Shopify has actually split that variant.
   *
   * 2026-10-15 (welcome link, QA): the keys in that list go stale mid-update.
   * The welcome code's Buy X get Y splits a subscription bag the moment it is
   * raised past 1 (1 + n), and re-splits the rest under a NEW key on every
   * write to it. Removing the row sent key A -> 0 (fine), then key B -> 0:
   * "no valid id or line parameter" - the trash button took off one bag. So
   * each step now re-reads the cart, finds the group by the same identity
   * cart-drawer.liquid groups on, and changes one CURRENT line until the sum
   * is the target. And the redraw is smk.js's variantCartRefresh(): it renders
   * the whole #CartDrawer, while refreshDawnCartUI() leaves the drawer to
   * Dawn's subscriber, which only swaps <cart-drawer-items> - the subtotal in
   * .cd-footer stayed at its old value.
   */
  if ('customElements' in window) {
    customElements.whenDefined('cart-drawer-items').then(function () {
      var CartDrawerItems = customElements.get('cart-drawer-items');
      if (!CartDrawerItems) return;
      if (Object.prototype.hasOwnProperty.call(CartDrawerItems.prototype, 'updateQuantity')) return;

      var originalUpdateQuantity = Object.getPrototypeOf(CartDrawerItems.prototype).updateQuantity;

      function changeLine(key, quantity) {
        return fetch(window.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity }),
        });
      }

      function readCart() {
        return fetch(window.routes.cart_url + '.js', {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        }).then(function (response) {
          return response.json();
        });
      }

      // variant + properties + selling plan: what cart-drawer.liquid groups on.
      function identity(item) {
        var props = item.properties || {};
        var names = Object.keys(props).sort();
        var plan = item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.id : 0;
        return (
          item.variant_id +
          '|' +
          names
            .map(function (name) {
              return name + '=' + props[name];
            })
            .join(';') +
          '|' +
          plan
        );
      }

      // One change per pass against the cart as it is NOW, until the group
      // sums to the target. Shrinks from the last line, so the line carrying
      // the Buy X get Y unit (the first) goes last. Capped: Shopify re-splits
      // once per write at most, so a handful of passes always settles.
      function settleGroup(id, target, passes) {
        return readCart().then(function (cart) {
          var lines = (cart.items || []).filter(function (item) {
            return identity(item) === id;
          });
          var sum = lines.reduce(function (total, item) {
            return total + item.quantity;
          }, 0);
          if (!lines.length || sum === target || passes <= 0) return;

          var last = lines[lines.length - 1];
          var quantity = Math.max(last.quantity + (target - sum), 0);
          return changeLine(last.key, quantity).then(function () {
            return settleGroup(id, target, passes - 1);
          });
        });
      }

      function redraw() {
        if (typeof window.variantCartRefresh === 'function') return window.variantCartRefresh();
        return window.refreshDawnCartUI ? window.refreshDawnCartUI() : null;
      }

      CartDrawerItems.prototype.updateQuantity = function (line, quantity, event, name, variantId) {
        var input = this.querySelector('#Quantity-' + line) || this.querySelector('#Drawer-quantity-' + line);
        var groupAttr = input && input.dataset.quantityGroupLines;
        var lines = null;
        if (groupAttr) {
          try {
            lines = JSON.parse(groupAttr);
          } catch (error) {
            lines = null;
          }
        }

        if (!lines || lines.length < 2) {
          return originalUpdateQuantity.call(this, line, quantity, event, name, variantId);
        }

        var self = this;
        self.enableLoading(line);

        var keys = lines.map(function (l) {
          return l.key;
        });

        return readCart()
          .then(function (cart) {
            var member = (cart.items || []).filter(function (item) {
              return keys.indexOf(item.key) !== -1;
            })[0];
            // The row's keys are all gone already: nothing to change here, just
            // show the cart as it is.
            if (!member) return;
            return settleGroup(identity(member), Number(quantity) || 0, 8);
          })
          .then(redraw)
          .catch(function (error) {
            console.warn('[variant] grouped quantity update failed', error);
          })
          .then(function () {
            self.disableLoading(line);
          });
      };
    });
  }

  /*
   * 6. Dawn's own loading state (enableLoading/disableLoading, above) only
   * ever disabled #CartDrawer-CartItems (pointer-events: none via
   * .cart__items--disabled) — the checkout button and the subscribe/upsell
   * slots sit outside that container and stayed clickable while a stepper
   * change, a removal, section 5's grouped update, or cart-gift.js's own
   * background gift reconciliation was still in flight. cart-gift.js in
   * particular runs with no loading indication at all today, so a shopper
   * could click "+" on a product line at the exact moment it was silently
   * adding or removing the free gift — precisely the kind of overlap this
   * session's other two fixes (the dismissal bug, the split-line bug) grew
   * out of.
   *
   * window.variantCartDrawerBusy(true/false) is the shared lock: it's a
   * counter, not a flag, so overlapping callers (a stepper change while
   * cart-gift.js is also mid-cycle) can't have one's "done" turn off the
   * other's "still working". CartDrawerItems.enableLoading/disableLoading
   * are wrapped here so every stepper, remove, and grouped update (section
   * 5) drives it automatically; cart-gift.js calls it directly around its
   * own writes, which Dawn's per-line loading state never touches.
   */
  var drawerBusyCount = 0;
  window.variantCartDrawerBusy = function (isBusy) {
    var drawer = document.getElementById('CartDrawer');
    drawerBusyCount = Math.max(0, drawerBusyCount + (isBusy ? 1 : -1));
    if (!drawer) return;
    drawer.classList.toggle('cd-busy', drawerBusyCount > 0);
    var checkout = drawer.querySelector('.cd-checkout');
    if (checkout) checkout.disabled = drawerBusyCount > 0;
  };

  if ('customElements' in window) {
    customElements.whenDefined('cart-drawer-items').then(function () {
      var CartDrawerItems = customElements.get('cart-drawer-items');
      if (!CartDrawerItems) return;
      if (CartDrawerItems.prototype.enableLoading && CartDrawerItems.prototype.enableLoading.__variant) return;

      var originalEnableLoading = CartDrawerItems.prototype.enableLoading;
      var originalDisableLoading = CartDrawerItems.prototype.disableLoading;

      CartDrawerItems.prototype.enableLoading = function (line) {
        window.variantCartDrawerBusy(true);
        return originalEnableLoading.call(this, line);
      };
      CartDrawerItems.prototype.enableLoading.__variant = true;

      CartDrawerItems.prototype.disableLoading = function (line) {
        window.variantCartDrawerBusy(false);
        return originalDisableLoading.call(this, line);
      };
    });
  }
})();
